/**
 * RewardPlayer - Lecteur de récompenses
 *
 * Affiche et joue les différents types de récompenses:
 * - Musique (Spotify, YouTube)
 * - Méditation guidée
 * - Exercices de respiration
 * - Pauses actives
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Play, Pause, Volume2, VolumeX, Maximize2, Minimize2,
  ExternalLink, Heart, ChevronDown, Clock, RotateCcw, Check
} from 'lucide-react';
import { haptic } from '../../../shared/utils/haptics';

/**
 * Lecteur YouTube intégré
 */
const YouTubePlayer = memo(function YouTubePlayer({ videoId, autoplay = true }) {
  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&rel=0&modestbranding=1`}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
});

/**
 * Lecteur Spotify intégré
 */
const SpotifyPlayer = memo(function SpotifyPlayer({ embedUrl }) {
  return (
    <div className="w-full rounded-xl overflow-hidden">
      <iframe
        src={embedUrl}
        width="100%"
        height="152"
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        className="rounded-xl"
      />
    </div>
  );
});

/**
 * Exercice de respiration guidé
 */
const BreathingExercise = memo(function BreathingExercise({ steps, cycles, onComplete }) {
  const [currentCycle, setCurrentCycle] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [timeLeft, setTimeLeft] = useState(steps[0]?.duration || 4);
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    if (!isRunning) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Passer à l'étape suivante
          const nextStep = currentStep + 1;

          if (nextStep >= steps.length) {
            // Cycle terminé
            const nextCycle = currentCycle + 1;

            if (nextCycle >= cycles) {
              // Exercice terminé
              clearInterval(timer);
              haptic.success();
              onComplete?.();
              return 0;
            }

            setCurrentCycle(nextCycle);
            setCurrentStep(0);
            return steps[0].duration;
          }

          setCurrentStep(nextStep);
          return steps[nextStep].duration;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, currentStep, currentCycle, steps, cycles, onComplete]);

  const currentAction = steps[currentStep]?.action || 'Respire';

  return (
    <div className="flex flex-col items-center py-8">
      {/* Cercle animé */}
      <div className="relative w-48 h-48 mb-8">
        {/* Cercle de fond */}
        <div className="absolute inset-0 rounded-full border-4 border-zinc-700" />

        {/* Cercle de progression */}
        <svg className="absolute inset-0 w-full h-full -rotate-90">
          <circle
            cx="96"
            cy="96"
            r="92"
            fill="none"
            stroke="url(#breathGradient)"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={578}
            strokeDashoffset={578 * (1 - timeLeft / steps[currentStep]?.duration)}
            className="transition-all duration-1000"
          />
          <defs>
            <linearGradient id="breathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
        </svg>

        {/* Contenu central */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span
            key={currentAction}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-2xl font-bold text-white mb-2"
          >
            {currentAction}
          </motion.span>
          <span className="text-4xl font-black text-cyan-400">{timeLeft}</span>
        </div>
      </div>

      {/* Indicateur de cycles */}
      <div className="flex items-center gap-2 mb-4">
        {Array.from({ length: cycles }).map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 rounded-full transition-colors ${
              i < currentCycle
                ? 'bg-cyan-500'
                : i === currentCycle
                ? 'bg-cyan-400 animate-pulse'
                : 'bg-zinc-700'
            }`}
          />
        ))}
      </div>

      <p className="text-zinc-400 text-sm">
        Cycle {currentCycle + 1} / {cycles}
      </p>

      {/* Bouton pause/play */}
      <button
        onClick={() => setIsRunning(!isRunning)}
        className="mt-6 w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center
          hover:bg-zinc-700 transition-colors"
      >
        {isRunning ? (
          <Pause className="w-5 h-5 text-white" />
        ) : (
          <Play className="w-5 h-5 text-white ml-0.5" />
        )}
      </button>
    </div>
  );
});

/**
 * Exercices guidés (étirements, etc.)
 */
const GuidedExercise = memo(function GuidedExercise({ exercises, onComplete }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(exercises[0]?.duration || 20);
  const [isRunning, setIsRunning] = useState(true);

  const currentExercise = exercises[currentIndex];

  useEffect(() => {
    if (!isRunning) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          const nextIndex = currentIndex + 1;

          if (nextIndex >= exercises.length) {
            clearInterval(timer);
            haptic.success();
            onComplete?.();
            return 0;
          }

          setCurrentIndex(nextIndex);
          haptic.medium();
          return exercises[nextIndex].duration;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, currentIndex, exercises, onComplete]);

  return (
    <div className="flex flex-col items-center py-8">
      {/* Progression */}
      <div className="flex items-center gap-1 mb-6">
        {exercises.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all ${
              i < currentIndex
                ? 'w-8 bg-emerald-500'
                : i === currentIndex
                ? 'w-8 bg-purple-500'
                : 'w-4 bg-zinc-700'
            }`}
          />
        ))}
      </div>

      {/* Exercice actuel */}
      <motion.div
        key={currentIndex}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h3 className="text-2xl font-bold text-white mb-3">
          {currentExercise?.name}
        </h3>
        <p className="text-zinc-400 mb-8 max-w-xs">
          {currentExercise?.instruction}
        </p>
      </motion.div>

      {/* Timer */}
      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20
        border-2 border-purple-500/30 flex items-center justify-center mb-6">
        <span className="text-4xl font-black text-white">{timeLeft}</span>
      </div>

      {/* Contrôles */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setIsRunning(!isRunning)}
          className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center
            hover:bg-zinc-700 transition-colors"
        >
          {isRunning ? (
            <Pause className="w-5 h-5 text-white" />
          ) : (
            <Play className="w-5 h-5 text-white ml-0.5" />
          )}
        </button>

        <button
          onClick={() => {
            if (currentIndex < exercises.length - 1) {
              setCurrentIndex(currentIndex + 1);
              setTimeLeft(exercises[currentIndex + 1].duration);
            }
          }}
          disabled={currentIndex >= exercises.length - 1}
          className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-400 text-sm
            hover:bg-zinc-700 transition-colors disabled:opacity-50"
        >
          Passer →
        </button>
      </div>
    </div>
  );
});

/**
 * Timer simple avec instruction
 */
const SimpleTimer = memo(function SimpleTimer({ duration, instruction, onComplete }) {
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    if (!isRunning || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          haptic.success();
          onComplete?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, timeLeft, onComplete]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="flex flex-col items-center py-8">
      <p className="text-zinc-400 text-center mb-8 max-w-xs">
        {instruction}
      </p>

      <div className="text-6xl font-black text-white mb-8">
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </div>

      <button
        onClick={() => setIsRunning(!isRunning)}
        className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500
          flex items-center justify-center hover:scale-105 transition-transform"
      >
        {isRunning ? (
          <Pause className="w-6 h-6 text-white" />
        ) : (
          <Play className="w-6 h-6 text-white ml-0.5" />
        )}
      </button>
    </div>
  );
});

/**
 * Citation motivante
 */
const MotivationalQuote = memo(function MotivationalQuote({ quotes, onComplete }) {
  const [quote] = useState(() => quotes[Math.floor(Math.random() * quotes.length)]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete?.();
    }, 5000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center py-12 px-6"
    >
      <div className="text-6xl mb-6">💭</div>
      <p className="text-xl text-white text-center font-medium leading-relaxed max-w-sm">
        "{quote}"
      </p>
      <button
        onClick={onComplete}
        className="mt-8 px-6 py-3 bg-zinc-800 rounded-xl text-white hover:bg-zinc-700 transition-colors"
      >
        Merci ! ✨
      </button>
    </motion.div>
  );
});

/**
 * Composant principal RewardPlayer
 */
export const RewardPlayer = memo(function RewardPlayer({
  reward,
  isPlaying,
  onClose,
  onToggleFavorite,
  isFavorite,
}) {
  const [isMinimized, setIsMinimized] = useState(false);

  // Handler de fermeture
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Handler de complétion
  const handleComplete = useCallback(() => {
    haptic.success();
    // On pourrait ajouter des XP bonus ici
  }, []);

  if (!reward || !isPlaying) return null;

  // Rendu selon le type de récompense
  const renderContent = () => {
    switch (reward.type) {
      case 'youtube':
        return <YouTubePlayer videoId={reward.videoId} />;

      case 'spotify':
        return <SpotifyPlayer embedUrl={reward.embedUrl} />;

      case 'guided':
        if (reward.steps) {
          return (
            <BreathingExercise
              steps={reward.steps}
              cycles={reward.cycles || 4}
              onComplete={handleComplete}
            />
          );
        }
        if (reward.exercises) {
          return (
            <GuidedExercise
              exercises={reward.exercises}
              onComplete={handleComplete}
            />
          );
        }
        if (reward.prompts) {
          return (
            <MotivationalQuote
              quotes={reward.prompts}
              onComplete={handleClose}
            />
          );
        }
        return null;

      case 'timer':
        return (
          <SimpleTimer
            duration={reward.duration}
            instruction={reward.instruction}
            onComplete={handleComplete}
          />
        );

      case 'reminder':
        return (
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <div className="text-6xl mb-6">{reward.icon}</div>
            <p className="text-xl text-white text-center font-medium mb-8">
              {reward.instruction}
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-emerald-500 rounded-xl text-white font-medium
                flex items-center gap-2 hover:bg-emerald-600 transition-colors"
            >
              <Check className="w-5 h-5" />
              C'est fait !
            </button>
          </div>
        );

      case 'quote':
        return (
          <MotivationalQuote
            quotes={reward.quotes}
            onComplete={handleClose}
          />
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">{reward.icon}</div>
            <h3 className="text-xl font-bold text-white mb-2">{reward.name}</h3>
            <p className="text-zinc-400">{reward.description}</p>
          </div>
        );
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed z-50 ${
          isMinimized
            ? 'bottom-20 right-4 w-16 h-16'
            : 'inset-0 bg-black/90 backdrop-blur-xl'
        }`}
      >
        {isMinimized ? (
          // Vue minimisée
          <button
            onClick={() => setIsMinimized(false)}
            className="w-full h-full rounded-full bg-gradient-to-br from-purple-500 to-pink-500
              flex items-center justify-center shadow-lg shadow-purple-500/30 animate-pulse"
          >
            <span className="text-2xl">{reward.icon}</span>
          </button>
        ) : (
          // Vue complète
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{reward.icon}</span>
                <div>
                  <h2 className="font-bold text-white">{reward.name}</h2>
                  {reward.duration > 0 && (
                    <p className="text-xs text-zinc-500">{reward.duration} min</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onToggleFavorite?.(reward.id)}
                  className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center"
                >
                  <Heart
                    className={`w-5 h-5 ${
                      isFavorite ? 'fill-red-500 text-red-500' : 'text-zinc-500'
                    }`}
                  />
                </button>

                <button
                  onClick={() => setIsMinimized(true)}
                  className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center"
                >
                  <Minimize2 className="w-5 h-5 text-zinc-500" />
                </button>

                <button
                  onClick={handleClose}
                  className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-zinc-500" />
                </button>
              </div>
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-y-auto p-4">
              {renderContent()}
            </div>

            {/* Lien externe pour Spotify/YouTube */}
            {reward.url && (
              <div className="p-4 border-t border-zinc-800">
                <a
                  href={reward.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3
                    bg-zinc-800 rounded-xl text-zinc-400 hover:text-white transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-sm">Ouvrir dans l'app</span>
                </a>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
});

export default RewardPlayer;
